import React, { useEffect, useState } from 'react';
import { auth, db, storage } from './firebase/config';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';

const s = {
  loading: 'Φόρτωση ρυθμίσεων…',
  title: 'Ρυθμίσεις εταιρείας',
  subtitle: 'Διαχειριστείτε τα στοιχεία και το branding της εταιρείας σας',
  accessDenied: 'Χωρίς πρόσβαση',
  accessDeniedBody: 'Μόνο ο ιδιοκτήτης της εταιρείας μπορεί να αλλάξει τις ρυθμίσεις.',
  backToDashboard: 'Επιστροφή στον πίνακα',
  logoSection: 'Λογότυπο εταιρείας',
  logoUploadLabel: 'Ανεβάστε το λογότυπο σας',
  logoChoose: 'Επιλογή αρχείου',
  logoHint: 'PNG, JPG, GIF έως 2 MB',
  basicInfo: 'Βασικές πληροφορίες',
  companyName: 'Επωνυμία εταιρείας*',
  companyNamePlaceholder: 'Επωνυμία εταιρείας',
  taxId: 'ΑΦΜ εταιρείας',
  taxIdPlaceholder: 'Αριθμός φορολογικού μητρώου',
  businessInfo: 'Επιπλέον στοιχεία',
  website: 'Ιστοσελίδα',
  websitePlaceholder: 'https://www.εταιρεια.gr',
  address: 'Διεύθυνση',
  addressPlaceholder: 'Οδός, Πόλη, ΤΚ, Χώρα',
  saving: 'Αποθήκευση…',
  save: 'Αποθήκευση αλλαγών',
  saved: 'Οι ρυθμίσεις αποθηκεύτηκαν!',
  saveFailed: 'Αποτυχία αποθήκευσης ρυθμίσεων.',
  loadFailed: 'Αποτυχία φόρτωσης δεδομένων.',
  notImage: 'Επιλέξτε αρχείο εικόνας.',
  imageTooLarge: 'Η εικόνα πρέπει να είναι έως 2 MB.',
  companyNameRequired: 'Η επωνυμία εταιρείας είναι υποχρεωτική.',
  noPermission: 'Μόνο ο ιδιοκτήτης μπορεί να αλλάξει ρυθμίσεις.',
};

export default function Settings() {
  const [companyName, setCompanyName] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: '', type: '' });
  const [address, setAddress] = useState('');
  const [taxId, setTaxId] = useState('');
  const [website, setWebsite] = useState('');
  const [userRole, setUserRole] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const user = auth.currentUser;

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    const checkUserRole = async () => {
      try {
        const companyDoc = await getDoc(doc(db, 'companies', user.uid));
        if (companyDoc.exists()) {
          setUserRole('owner');
          setCompanyId(user.uid);
          fetchCompanyData(user.uid);
        } else {
          const memberDocs = await getDocs(collection(db, 'userMemberships'));
          let found = false;
          memberDocs.forEach((d) => {
            if (d.id === user.uid) {
              found = true;
              const data = d.data();
              setCompanyId(data.companyId);
              setUserRole(data.role);
              fetchCompanyData(data.companyId);
            }
          });
          if (!found) { showToast(s.loadFailed, 'error'); navigate('/'); }
        }
      } catch { showToast(s.loadFailed, 'error'); }
    };
    checkUserRole();
  }, [user, navigate]);

  const fetchCompanyData = async (id) => {
    try {
      setLoading(true);
      const snap = await getDoc(doc(db, 'companies', id));
      if (snap.exists()) {
        const d = snap.data();
        setCompanyName(d.companyName || '');
        setLogoUrl(d.logoUrl || null);
        setAddress(d.address || '');
        setTaxId(d.taxId || '');
        setWebsite(d.website || '');
      }
    } catch { showToast(s.loadFailed, 'error'); }
    finally { setLoading(false); }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showToast(s.notImage, 'error'); return; }
    if (file.size > 2 * 1024 * 1024) { showToast(s.imageTooLarge, 'error'); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!user || !companyId) return;
    if (!companyName.trim()) { showToast(s.companyNameRequired, 'error'); return; }
    if (userRole !== 'owner') { showToast(s.noPermission, 'error'); return; }
    try {
      setUploading(true);
      let newLogoUrl = logoUrl;
      if (logoFile) {
        const fileName = `logo_${Date.now()}_${logoFile.name.replace(/\s+/g, '_')}`;
        const storageRef = ref(storage, `logos/${companyId}/${fileName}`);
        await uploadBytes(storageRef, logoFile);
        newLogoUrl = await getDownloadURL(storageRef);
      }
      await updateDoc(doc(db, 'companies', companyId), {
        companyName, logoUrl: newLogoUrl, address, taxId, website,
        updatedAt: new Date().toISOString()
      });
      setLogoUrl(newLogoUrl);
      setLogoFile(null);
      setLogoPreview(null);
      showToast(s.saved, 'success');
    } catch { showToast(s.saveFailed, 'error'); }
    finally { setUploading(false); }
  };

  const showToast = (message, type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast({ visible: false, message: '', type: '' }), 3000);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setLogoUrl(null);
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto py-10">
        <div className="bg-white shadow rounded-lg p-8 flex items-center justify-center">
          <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="ml-2 text-gray-600">{s.loading}</span>
        </div>
      </div>
    );
  }

  if (userRole !== 'owner') {
    return (
      <div className="max-w-3xl mx-auto py-10">
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{s.accessDenied}</h2>
          <p className="text-gray-600 mb-4">{s.accessDeniedBody}</p>
          <button onClick={() => navigate('/')} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
            {s.backToDashboard}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-6">
      {toast.visible && (
        <div className={`fixed top-4 right-4 px-4 py-3 rounded shadow-lg z-50 ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white`}>
          {toast.message}
        </div>
      )}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="bg-blue-600 px-6 py-4">
          <h2 className="text-xl font-bold text-white">{s.title}</h2>
          <p className="text-blue-100 text-sm">{s.subtitle}</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Logo */}
            <div className="md:col-span-2">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">{s.logoSection}</h3>
              <div className="flex items-center space-x-6">
                <div className="flex-shrink-0">
                  {logoPreview || logoUrl ? (
                    <div className="relative">
                      <img src={logoPreview || logoUrl} alt="Logo" className="w-24 h-24 object-cover rounded-lg border border-gray-200" />
                      <button onClick={handleRemoveLogo} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 rounded-lg bg-gray-100 border border-dashed border-gray-300 flex items-center justify-center text-gray-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="flex-grow">
                  <label className="block mb-2 text-sm font-medium text-gray-700">{s.logoUploadLabel}</label>
                  <label className="cursor-pointer bg-white border border-gray-300 rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                    {s.logoChoose}
                    <input type="file" accept="image/*" onChange={handleLogoChange} className="sr-only" />
                  </label>
                  <p className="mt-1 text-xs text-gray-500">{s.logoHint}</p>
                </div>
              </div>
            </div>

            {/* Basic Info */}
            <div className="md:col-span-2">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">{s.basicInfo}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="companyName" className="block mb-1 text-sm font-medium text-gray-700">{s.companyName}</label>
                  <input id="companyName" type="text" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder={s.companyNamePlaceholder} />
                </div>
                <div>
                  <label htmlFor="taxId" className="block mb-1 text-sm font-medium text-gray-700">{s.taxId}</label>
                  <input id="taxId" type="text" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={taxId} onChange={(e) => setTaxId(e.target.value)} placeholder={s.taxIdPlaceholder} />
                </div>
              </div>
            </div>

            {/* Business Info */}
            <div className="md:col-span-2">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">{s.businessInfo}</h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label htmlFor="website" className="block mb-1 text-sm font-medium text-gray-700">{s.website}</label>
                  <input id="website" type="url" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder={s.websitePlaceholder} />
                </div>
                <div>
                  <label htmlFor="address" className="block mb-1 text-sm font-medium text-gray-700">{s.address}</label>
                  <textarea id="address" rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={address} onChange={(e) => setAddress(e.target.value)} placeholder={s.addressPlaceholder} />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-5 border-t border-gray-200 flex justify-end">
            <button onClick={handleSave} disabled={uploading} className="flex items-center px-5 py-2.5 bg-blue-600 text-white font-medium rounded-md shadow-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
              {uploading && (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              {uploading ? s.saving : s.save}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
